import { useCallback, useEffect, useState } from "react";
import {
  AlfredFiatAccount,
  AlfredFiatAccountFields,
  AlfredFiatAccountType,
  AlfredInitiateWithdrawalTransaction,
  alfredService,
  PaymentType,
  v4Service,
} from "src/api";
import { useAlfredQuotePreview, useAlfredSupportedOptions, useEstimatePaymentFee, usePaymentValidation } from "src/hooks";
import { useDepositWithdrawContext, useUrlParamsContext } from "src/contexts";
import { onlyNumbers, formatNumberToExchange, _t } from "src/utils";

/**
 * Alfred-only withdrawal orchestrator. Mirrors `useDeposit` in shape:
 *
 *  1. Pick a target fiat (`selectedOption`) — what the user receives.
 *  2. Type a USDC amount (`amount`) — what they spend.
 *  3. Pick one of the saved Alfred payment methods.
 *  4. Confirm — backend mints the offramp at Alfred and (on dev
 *     exchanges) auto-fires the success-path webhook chain so the
 *     stepper marches to FIAT_TRANSFER_COMPLETED. The widget polls
 *     `GET /psp/alfred/session` for live status.
 *
 * Quote direction is the inverse of deposit: source = USDC (stable),
 * target = fiat. Platform fee is computed on the USDC side (route's
 * stable currency, same as deposit).
 */
export const useAlfredWithdrawal = () => {
  const { balance } = useDepositWithdrawContext();
  const { paymentRouteId } = useUrlParamsContext();
  const { currency } = balance;

  const [amount, setAmount] = useState<string>("");
  const [amountTouched, setAmountTouched] = useState<boolean>(false);
  const [selectedFiatAccountId, setSelectedFiatAccountId] = useState<string | null>(null);
  const [fiatAccounts, setFiatAccounts] = useState<AlfredFiatAccount[]>([]);
  const [fiatAccountsLoading, setFiatAccountsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [transaction, setTransaction] = useState<AlfredInitiateWithdrawalTransaction | null>(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Add-bank-account inline form state. We keep it on the same hook so
  // the form lives alongside the dropdown — Alfred stores the account,
  // we just refresh the list and auto-select the new entry.
  const [addBankOpen, setAddBankOpen] = useState(false);
  const [addBankLoading, setAddBankLoading] = useState(false);
  const [addBankError, setAddBankError] = useState<string | null>(null);

  const {
    options,
    selected: selectedOption,
    selectedCurrency,
    setSelectedCurrency,
    loading: optionsLoading,
  } = useAlfredSupportedOptions({ type: "withdrawal" });

  // Quote direction: USDC → fiat. `sourceCurrency` here is the wallet
  // currency (stable, route's currency_id) and `targetCurrency` is the
  // user-picked fiat. `useAlfredQuotePreview` is direction-agnostic.
  const { quote, loading: quoteLoading } = useAlfredQuotePreview({
    sourceCurrency: currency.currency_id || null,
    targetCurrency: selectedOption?.source_currency ?? null,
    amount,
  });
  const fiatReceiveGross = quote ? Number(quote.toAmount) : 0;
  const stableSpend = Number(amount) || 0;

  // Platform fee runs against the USDC side — same way deposit does.
  // `paymentType: Withdrawal` flips the right column on the fee_group.
  const { paymentFee, paymentFeeLoading, paymentFeeError } = useEstimatePaymentFee({
    paymentType: PaymentType.Withdrawal,
    amount: stableSpend,
  });

  const { paymentValidationErrors } = usePaymentValidation({
    amount: stableSpend,
    paymentType: PaymentType.Withdrawal,
    fee: paymentFee,
  });

  const handleAmountChange = (next: string) => {
    if (onlyNumbers(next)) {
      setAmount(formatNumberToExchange(next, currency.precision));
    }
  };

  // Lazy-loads beneficiary fiatAccounts once the user picks a fiat. We
  // need a session_id first because the listFiatAccounts endpoint binds
  // the customer via the cached session — so we create it here too.
  const ensureSessionAndLoadAccounts = useCallback(async () => {
    if (!selectedCurrency || sessionId) return;
    setFiatAccountsLoading(true);
    try {
      const session = await v4Service.createSession({
        data: {
          amount: Number(amount) || 1,
          payment_route_id: paymentRouteId,
          source_currency_id: selectedCurrency,
          type: PaymentType.Withdrawal,
        },
      });
      if (!session?.session_id) {
        throw new Error(_t("ERRORS.FAILED_TO_CREATE_SESSION"));
      }
      setSessionId(session.session_id);
      const accounts = await alfredService.listFiatAccounts(session.session_id);
      setFiatAccounts(accounts);
      // Default to the first account; status semantics on Alfred's
      // fiatAccount controller are not as strict as the legacy
      // payment-method `active` filter, so we don't pre-filter here.
      if (accounts[0]) setSelectedFiatAccountId(accounts[0].fiatAccountId);
    } catch (e) {
      console.error("Failed to load Alfred fiat accounts:", e);
    } finally {
      setFiatAccountsLoading(false);
    }
  }, [selectedCurrency, sessionId, paymentRouteId, amount]);

  useEffect(() => {
    void ensureSessionAndLoadAccounts();
  }, [ensureSessionAndLoadAccounts]);

  // Bumps the session amount each time the user changes the input, so
  // /initiate-withdrawal reads the right value at submit time. The
  // session was created with a placeholder amount above.
  useEffect(() => {
    if (!sessionId || !amount || !selectedCurrency) return;
    const numeric = Number(amount);
    if (!Number.isFinite(numeric) || numeric <= 0) return;
    v4Service
      .createSession({
        data: {
          amount: numeric,
          payment_route_id: paymentRouteId,
          source_currency_id: selectedCurrency,
          type: PaymentType.Withdrawal,
        },
      })
      .then((s) => s?.session_id && setSessionId(s.session_id))
      .catch(() => undefined);
  }, [amount, selectedCurrency, paymentRouteId, sessionId]);

  const handleInitiateWithdrawal = async (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    if (!sessionId || !selectedFiatAccountId) {
      setSubmitError(_t("ERRORS.MISSING_REQUIRED_DATA_FOR_WITHDRAWAL"));
      return;
    }
    setSubmitLoading(true);
    setSubmitError(null);
    try {
      const res = await alfredService.initiateWithdrawal({
        session_id: sessionId,
        fiat_account_id: selectedFiatAccountId,
      });
      setTransaction(res.transaction);
    } catch (err) {
      console.error("Alfred initiateWithdrawal failed:", err);
      setSubmitError(_t("ERRORS.FAILED_TO_PROCESS_WITHDRAWAL"));
    } finally {
      setSubmitLoading(false);
    }
  };

  const resetWithdrawal = () => {
    setTransaction(null);
    setAmount("");
    setSubmitError(null);
  };

  const refreshFiatAccounts = useCallback(
    async (sid: string, prefer?: string) => {
      const accounts = await alfredService.listFiatAccounts(sid);
      setFiatAccounts(accounts);
      const next =
        (prefer && accounts.find((a) => a.fiatAccountId === prefer)) ??
        accounts[0] ??
        null;
      if (next) setSelectedFiatAccountId(next.fiatAccountId);
    },
    [],
  );

  const handleAddFiatAccount = useCallback(
    async (payload: { type: AlfredFiatAccountType; fiatAccountFields: AlfredFiatAccountFields; isExternal?: boolean }) => {
      if (!sessionId) {
        setAddBankError(_t("ERRORS.MISSING_REQUIRED_DATA_FOR_WITHDRAWAL"));
        return;
      }
      setAddBankLoading(true);
      setAddBankError(null);
      try {
        const created = await alfredService.createFiatAccount({
          session_id: sessionId,
          ...payload,
        });
        await refreshFiatAccounts(sessionId, created.fiatAccountId);
        setAddBankOpen(false);
      } catch (err: any) {
        // Surface Alfred's `errorMessage` (and the offending field set
        // when the validator returns errorCode 110002) so the user
        // sees exactly which field needs fixing instead of a generic
        // "request failed".
        const apiMsg =
          err?.response?.data?.errorMessage ??
          err?.message ??
          _t("ERRORS.GENERIC");
        const fields = err?.response?.data?.errorMetadata?.fields;
        setAddBankError(fields?.length ? `${apiMsg}: ${fields.join(", ")}` : String(apiMsg));
      } finally {
        setAddBankLoading(false);
      }
    },
    [sessionId, refreshFiatAccounts],
  );

  const errors = {
    inputErrors: [
      ...(!amount ? [_t("VALIDATION.AMOUNT_SHOULD_NOT_BE_EMPTY")] : []),
      ...(stableSpend <= 0 ? [_t("VALIDATION.AMOUNT_SHOULD_BE_GREATER_THAN_ZERO")] : []),
    ],
    paymentValidationErrors,
    paymentMethodErrors: !selectedFiatAccountId ? [_t("VALIDATION.PAYMENT_METHOD_REQUIRED")] : [],
    paymentFeeErrors: paymentFeeError ? [_t("VALIDATION.ERROR_ESTIMATING_PAYMENT_FEE")] : [],
    balanceErrors:
      stableSpend > 0 && stableSpend > balance.free_balance
        ? [_t("VALIDATION.NOT_ENOUGH_BALANCE", { balance: balance.free_balance })]
        : [],
  };

  const submitDisabled =
    paymentFeeLoading ||
    quoteLoading ||
    submitLoading ||
    !sessionId ||
    !selectedFiatAccountId ||
    Object.values(errors).some((e) => e.length > 0);

  return {
    amount,
    amountTouched,
    setAmountTouched,
    handleAmountChange,
    options,
    selectedOption,
    selectedCurrency,
    setSelectedCurrency,
    optionsLoading,
    fiatAccounts,
    fiatAccountsLoading,
    selectedFiatAccountId,
    setSelectedFiatAccountId,
    addBankOpen,
    setAddBankOpen,
    addBankLoading,
    addBankError,
    handleAddFiatAccount,
    quote,
    quoteLoading,
    fiatReceiveGross,
    netStableSpend: stableSpend + paymentFee,
    paymentFee,
    paymentFeeLoading,
    balance,
    currency,
    errors,
    submitDisabled,
    submitLoading,
    submitError,
    handleInitiateWithdrawal,
    sessionId,
    transaction,
    resetWithdrawal,
  };
};
